import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  DocumentTextIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import Topbar from '../../PulseSurvey/Topbar';
import { getLocalStorageItem, errorToast } from '../../../utils/helper';
import { CompanyApi } from '../../../api/companyApi';
import Loader from '../../../component/common/Loader';

/**
 * Orders Component
 * View and manage marketplace orders
 * @returns {JSX.Element} Orders component
 */
const Orders = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const limit = 10;

  // Get user data from localStorage
  const userData = getLocalStorageItem('userData')
    ? JSON.parse(getLocalStorageItem('userData'))
    : null;
  const slug = userData?.slug || 'b2b';

  /**
   * Fetch orders from API
   */
  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await CompanyApi.getCompanyOrders(currentPage, limit);

      if (response?.data) {
        setOrders(response.data.data || []);
        setTotalOrders(response.data.meta?.totalCount || 0);
        setTotalPages(response.data.meta?.totalPages || 1);
      } else {
        setError('Failed to load orders');
      }
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError(err?.response?.data?.message || 'Failed to load orders');
      errorToast(err?.response?.data?.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [currentPage, limit]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const fetchInfo = () => {
    console.log('fetchInfo function in Orders page');
  };

  /**
   * Handle page change
   */
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  /**
   * Format date to readable string
   */
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  /**
   * Format currency
   */
  const formatCurrency = (amount, currency = 'GBP') => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  /**
   * Calculate total amount from items
   */
  const calculateOrderTotal = (items) => {
    if (!items || !Array.isArray(items)) return 0;
    return items.reduce((total, item) => total + item.quantity * item.price, 0);
  };

  /**
   * Get items summary
   */
  const getItemsSummary = (items) => {
    if (!items || !Array.isArray(items)) return 'No items';
    const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
    return `${totalItems} item${totalItems !== 1 ? 's' : ''}`;
  };

  return (
    <>
      <div className="relative lg:mb-[45px]">
        <Helmet>
          <meta charSet="utf-8" />
          <title>Orders | Shoorah Admin</title>
        </Helmet>
        <Topbar title={'My Orders'} infoBtn={false} setOpen={setOpen} fetchInfo={fetchInfo} />

        {/* Back Button */}
        <div className="mt-6">
          <button
            onClick={() => navigate(`/${slug}/marketplace`)}
            className="inline-flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors duration-200"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to Marketplace
          </button>
        </div>

        {/* Orders Content */}
        <div className="mt-6">
          <div className="bg-white dark:bg-shoorah-darkBgTabColor shadow rounded-lg">
            <div className="px-6 py-8">
              {loading ? (
                <div className="flex justify-center items-center py-12">
                  <Loader />
                </div>
              ) : error ? (
                <div className="text-center py-12">
                  <div className="mx-auto h-16 w-16 text-red-500 mb-4">
                    <svg
                      className="w-full h-full"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                  </div>
                  <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
                    {error}
                  </h3>
                  <button
                    onClick={fetchOrders}
                    className="mt-6 inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors duration-200"
                  >
                    Try Again
                  </button>
                </div>
              ) : orders.length === 0 ? (
                <div className="text-center py-12">
                  <DocumentTextIcon className="mx-auto h-16 w-16 text-gray-400" />
                  <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
                    No orders yet
                  </h3>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    Your order history will appear here once you make a purchase
                  </p>
                  <button
                    onClick={() => navigate(`/${slug}/marketplace`)}
                    className="mt-6 inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors duration-200"
                  >
                    Start Shopping
                  </button>
                </div>
              ) : (
                <div>
                  {/* Orders Table */}
                  <div className="mb-4 flex justify-between items-center">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Order History ({totalOrders} {totalOrders === 1 ? 'order' : 'orders'})
                    </h2>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-shoorah-darkBorder">
                      <thead className="bg-gray-50 dark:bg-gray-800">
                        <tr>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                          >
                            Order Reference
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                          >
                            Items
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                          >
                            Total Amount
                          </th>

                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                          >
                            Date
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-shoorah-darkBgTabColor divide-y divide-gray-200 dark:divide-shoorah-darkBorder">
                        {orders.map((order, index) => (
                          <tr
                            key={order._id || index}
                            className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-150"
                          >
                            <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">
                              <div className="font-semibold">{order.order_reference || 'N/A'}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                ID: {order._id?.slice(-8) || 'N/A'}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-900 dark:text-white">
                                <div className="font-medium mb-2">
                                  {getItemsSummary(order.items)}
                                </div>
                                {order.items && order.items.length > 0 && (
                                  <div className="space-y-1">
                                    {order.items.map((item, idx) => (
                                      <div
                                        key={idx}
                                        className="text-xs text-gray-600 dark:text-gray-400"
                                      >
                                        <span className="font-medium">{item.code}</span>
                                        {' - '}
                                        <span>Qty: {item.quantity}</span>
                                        {' × '}
                                        <span>{formatCurrency(item.price)}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 dark:text-white">
                              {formatCurrency(calculateOrderTotal(order.items))}
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {order.createdAt ? formatDate(order.createdAt) : 'N/A'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="mt-6 flex items-center justify-between">
                      <div className="text-sm text-gray-700 dark:text-gray-300">
                        Showing page {currentPage} of {totalPages}
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className={`inline-flex items-center px-3 py-2 border rounded-md text-sm font-medium ${
                            currentPage === 1
                              ? 'border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                              : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
                          }`}
                        >
                          <ChevronLeftIcon className="h-4 w-4 mr-1" />
                          Previous
                        </button>
                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className={`inline-flex items-center px-3 py-2 border rounded-md text-sm font-medium ${
                            currentPage === totalPages
                              ? 'border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                              : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
                          }`}
                        >
                          Next
                          <ChevronRightIcon className="h-4 w-4 ml-1" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Orders;
